import { useFetch } from "./api/useFetch";
import { adaptStats } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge } from "./ui-badges";
import {
  Loader2,
  AlertOctagon,
  BarChart3,
  Package,
  Trash2,
  ClipboardList,
  Bell,
  Info,
} from "lucide-react";

const SECTION_META = {
  inventory: {
    title: "Inventory activity",
    icon: Package,
    operationalQuestion:
      "How much catalog/inventory data exists for this tenant right now?",
  },
  removals: {
    title: "Removals activity",
    icon: Trash2,
    operationalQuestion:
      "How often is inventory being removed/expired/discarded?",
  },
  productRequests: {
    title: "Product requests",
    icon: ClipboardList,
    operationalQuestion:
      "What is the volume and status of product requests for this tenant?",
  },
  notifications: {
    title: "Notification health",
    icon: Bell,
    operationalQuestion:
      "Are notifications flowing for this tenant — counts, failures, channels?",
  },
};

// Format a single key/value pair from the upstream stats sub-object.
// Numbers are formatted with locale separators; ISO timestamps are kept as-is.
const formatStatValue = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") return v;
  // Nested object — show a compact JSON preview so operators see *something*
  // truthful rather than a hidden value.
  try {
    return JSON.stringify(v);
  } catch (e) {
    return String(v);
  }
};

// Convert "open_count" / "openCount" → "Open count" for display.
const humanizeKey = (k) =>
  String(k)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());

const StatsSection = ({ sectionKey, data }) => {
  const meta = SECTION_META[sectionKey];
  const Icon = meta.icon;
  const hasData = data && Object.keys(data).length > 0;

  return (
    <div
      data-testid={`stats-section-${sectionKey}`}
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0d3b66]/10 text-[#0d3b66] shrink-0">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <h4
              data-testid={`stats-section-${sectionKey}-title`}
              className="text-sm font-semibold text-slate-900 truncate"
            >
              {meta.title}
            </h4>
            <p className="text-[11px] text-slate-500">
              {meta.operationalQuestion}
            </p>
          </div>
        </div>
        <span
          data-testid={`stats-section-${sectionKey}-source`}
          className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0"
          title="GET /api/platform/companies/{id}/stats"
        >
          Live · ShelfLife
        </span>
      </div>

      {hasData ? (
        <dl
          data-testid={`stats-section-${sectionKey}-fields`}
          className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs"
        >
          {Object.entries(data).map(([k, v]) => (
            <div
              key={k}
              data-testid={`stats-section-${sectionKey}-field-${k}`}
              className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1"
            >
              <dt className="text-slate-500 truncate" title={k}>
                {humanizeKey(k)}
              </dt>
              <dd className="font-semibold text-slate-900 tabular-nums shrink-0">
                {formatStatValue(v)}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <div
          data-testid={`stats-section-${sectionKey}-unavailable`}
          className="flex items-start gap-2 rounded bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-900"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            ShelfLife did not report a{" "}
            <span className="font-mono">{sectionKey}</span> object for this
            tenant. No mock data is shown.
          </span>
        </div>
      )}
    </div>
  );
};

export const PlatformStats = ({ companyId, compact }) => {
  const live = isSurfaceLive("STATS");
  const { data, error, loading } = useFetch(
    live && companyId ? `/api/platform/companies/${companyId}/stats` : null
  );
  const stats = data ? adaptStats(data) : null;

  return (
    <div
      data-testid={`platform-stats-${companyId || "none"}`}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Company stats</h3>
          {!compact && (
            <p className="text-xs text-slate-500 mt-0.5">
              Read-only platform aggregates.
            </p>
          )}
        </div>
        <LiveBadge live={live} />
      </div>

      {!live && (
        <p className="text-xs text-slate-500 text-center py-4">
          Stats are mock until <span className="font-mono">REACT_APP_LIVE_STATS=true</span>.
        </p>
      )}

      {live && loading && (
        <div className="py-6 text-center">
          <Loader2 className="mx-auto h-5 w-5 text-slate-400 animate-spin" />
        </div>
      )}

      {live && error && (
        <div className="text-xs text-rose-700 flex items-start gap-2">
          <AlertOctagon className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error.kind === "forbidden" ? "Not authorized" : error.message}</span>
        </div>
      )}

      {live && stats && !error && (
        <>
          <div
            data-testid="platform-stats-tiles"
            className="grid grid-cols-2 gap-2 text-sm"
          >
            <Tile label="Stores" value={stats.storeCount} />
            <Tile label="Users" value={stats.userCount} />
            <Tile label="Departments" value={stats.departmentCount} />
            <Tile label="Active stores" value={stats.activeStores} />
            {/* Truthful product-request label (was incorrectly "Pending invites") */}
            <Tile
              label="Pending product requests"
              value={
                stats.pendingProductRequests !== null
                  ? stats.pendingProductRequests.toLocaleString()
                  : "Not reported"
              }
              testId="tile-pending-product-requests"
              hint="product_requests.pending"
              truthful={stats.pendingProductRequests === null}
            />
            {/* Catalog product count is intentionally separate from
                inventory batches; ShelfLife often doesn't report it. */}
            <Tile
              label="Catalog product count"
              value={
                stats.catalogProductCount !== null
                  ? stats.catalogProductCount.toLocaleString()
                  : "Not reported"
              }
              testId="tile-catalog-product-count"
              hint="stats.catalog_item_count"
              truthful={stats.catalogProductCount === null}
            />
            {stats.lastActivity && stats.lastActivity !== "—" && (
              <div className="col-span-2 mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                Last activity:{" "}
                <span className="font-medium text-slate-700">
                  {stats.lastActivity}
                </span>
              </div>
            )}
          </div>

          {!compact && (
            <div
              data-testid="platform-stats-inventory-batches"
              className="mt-4 grid grid-cols-3 gap-2 text-sm"
            >
              <Tile
                label="Inventory batches (all-time)"
                value={
                  stats.inventoryBatchesTotal !== null
                    ? stats.inventoryBatchesTotal.toLocaleString()
                    : "Not reported"
                }
                testId="tile-inventory-batches-total"
                hint="inventory.total"
                truthful={stats.inventoryBatchesTotal === null}
              />
              <Tile
                label="Active inventory batches"
                value={
                  stats.inventoryBatchesActive !== null
                    ? stats.inventoryBatchesActive.toLocaleString()
                    : "Not reported"
                }
                testId="tile-inventory-batches-active"
                hint="inventory.active"
                truthful={stats.inventoryBatchesActive === null}
              />
              <Tile
                label="Historical removed batches"
                value={
                  stats.inventoryBatchesHistorical !== null
                    ? stats.inventoryBatchesHistorical.toLocaleString()
                    : "Not reported"
                }
                testId="tile-inventory-batches-historical"
                hint="inventory.total − inventory.active"
                truthful={stats.inventoryBatchesHistorical === null}
              />
            </div>
          )}

          {!compact && (
            <div
              data-testid="platform-stats-sections"
              className="mt-4 grid grid-cols-1 gap-3"
            >
              <StatsSection sectionKey="inventory" data={stats.inventory} />
              <StatsSection sectionKey="removals" data={stats.removals} />
              <StatsSection
                sectionKey="productRequests"
                data={stats.productRequests}
              />
              <StatsSection
                sectionKey="notifications"
                data={stats.notifications}
              />
              <p className="text-[10px] text-slate-400 text-right">
                Source:{" "}
                <span className="font-mono">
                  GET /api/platform/companies/{companyId}/stats
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Tile = ({ label, value, sub, testId, hint, truthful }) => (
  <div
    data-testid={testId}
    className="rounded-lg bg-slate-50 px-3 py-2"
    title={hint || undefined}
  >
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div
      className={
        truthful
          ? "mt-0.5 text-xs italic text-slate-400"
          : "mt-0.5 text-sm font-semibold text-slate-900 tabular-nums"
      }
    >
      {value}
    </div>
    {sub && <div className="mt-0.5 text-[10px] text-slate-500">{sub}</div>}
  </div>
);
